import {
  GetNodeEntriesOptions,
  insertData,
  isEditor,
  withoutSavingHistory,
} from '@udecode/plate';
import { parseTextToNodes } from '~/editor/BufferEditor/parseTextToNodes';
import {
  resetEditorText,
  getPlainText as getSlatePlainText,
  serialize,
} from '~/editor/BufferEditor';
import { BufferEditor, BufferValue } from '~/editor/BufferEditor/types.plate';
import { LinkedInEntityDetails } from '~/editor/plugins/linkedin-annotations/nodes/LinkedInAnnotationElement';

import { EditorEntityData } from '~/editor/helpers/EditorEntityData';
import { Service } from '~/post/types.generated';
import { getAllFacebookMentions } from '~/editor/plugins/facebook-mention/commands/getAllFacebookMentions';
import { getAllLinkNodes } from '~/editor/plugins/link/nodes/getAllLinkNodes';
import { LinkElement } from '~/editor/plugins/link/nodes/LinkElement';
import type { SlateDraft } from '../Draft';

function isEditorOrState(
  editorOrState: Draft.EditorState | BufferEditor
): editorOrState is BufferEditor {
  return isEditor(editorOrState);
}

function createStateFromText(
  editor: BufferEditor,
  text = '',
  _serviceId?: Service,
  data?: EditorEntityData
): BufferEditor {
  withoutSavingHistory(editor, () => {
    resetEditorText(editor);
    parseTextToNodes(editor, text, data);
  });

  return editor;
}

export type GetPlainTextOptions = GetNodeEntriesOptions<BufferValue>;
function getPlainText(
  editor: BufferEditor,
  options: GetPlainTextOptions = {}
): string {
  return getSlatePlainText(editor, options);
}

function hasText(
  editor: BufferEditor,
  options: GetNodeEntriesOptions<BufferValue> = {}
): boolean {
  return !!getSlatePlainText(editor, options).trim();
}

function hasUnshortenedLinks(editor: BufferEditor): boolean {
  return getAllLinkNodes(editor).some(([node]) =>
    LinkElement.hasBeenUnshortened(node)
  );
}

function parseLinks(editor: BufferEditor): string[] {
  const nodes = getAllLinkNodes(editor);

  return nodes.map(([node]) => LinkElement.getText(node));
}

function copyDraftEditorState(
  draftFrom: SlateDraft,
  draftTo: SlateDraft
): BufferEditor {
  const { text, data } = serialize(draftFrom.editorState);
  return createStateFromText(draftTo.editorState, text, undefined, data);
}

function getFacebookAutocompleteEntities(editor: BufferEditor) {
  return getAllFacebookMentions(editor);
}

function todo(method: string, mockReturn: unknown = undefined): unknown {
  return function (editor) {
    // eslint-disable-next-line no-console
    console.warn(`TODO: add implementation of ${method} in SlateJsProxy`);

    return mockReturn || editor;
  };
}

export const SlateJsProxy = {
  isEditorOrState,
  createStateFromText,
  getPlainText,
  hasText,
  hasUnshortenedLinks,
  parseLinks,
  copyDraftEditorState,
  getFacebookAutocompleteEntities,
  removeFacebookAutocompleteEntities: todo(
    'removeFacebookAutocompleteEntities'
  ),
  getAllLinkedinAnnotations: (editor) => {
    return editor.getAllLinkedInAnnotations?.(editor);
  },
  insertLinkedInAnnotation: (editor, annotation: LinkedInEntityDetails) => {
    editor.insertLinkedInAnnotation?.(editor, annotation);

    return editor;
  },
  applyLinkedinAnnotation: todo('applyLinkedinAnnotation'),
  resetEditorState: todo('resetEditorState'),
  findAndCreateTwitterHashtags: todo('findAndCreateTwitterHashtags'),
  findAndCreateFacebookMentions: todo('findAndCreateFacebookMentions'),
  replaceLinkWithShortLink: (
    editor: BufferEditor,
    link: string,
    shortLink: string
  ): BufferEditor => {
    editor.replaceLinkWithShortLink?.(editor, link, shortLink);
    return editor;
  },
  replaceLinkWithUnshortenedLink: (
    editor: BufferEditor,
    unshortenedLink: string,
    shortLink: string
  ): BufferEditor => {
    editor.replaceLinkWithUnshortenedLink?.(editor, unshortenedLink, shortLink);
    return editor;
  },
  findAndCreateTwitterMentions: todo('findAndCreateTwitterMentions'),
  createEditorStateFromText: todo('createEditorStateFromText'),
  insertHashtag: (editor: BufferEditor, text: string) => {
    const data = new DataTransfer();
    data.setData('text/plain', text);
    insertData(editor, data);
    return editor;
  },
};

export type SlateJsProxyType = typeof SlateJsProxy;
